import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileSpreadsheet, Download, Users, MessageSquare, TrendingUp, Target,
  Calendar, Filter, Loader2, Eye, FileText, Table
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  {
    id: 'contacts',
    name: 'Contactos',
    description: 'Lista completa de contactos con datos de contacto y estado',
    icon: Users,
    color: 'bg-blue-100 text-blue-600'
  },
  {
    id: 'conversations_by_stage',
    name: 'Conversaciones por Etapa',
    description: 'Detalle de conversaciones agrupadas por etapa del pipeline',
    icon: MessageSquare,
    color: 'bg-green-100 text-green-600'
  },
  {
    id: 'agent_performance',
    name: 'Rendimiento por Agente',
    description: 'Métricas de desempeño de cada agente del equipo',
    icon: TrendingUp,
    color: 'bg-purple-100 text-purple-600'
  },
  {
    id: 'leads_outcome',
    name: 'Leads Ganados/Perdidos',
    description: 'Resultados finales de leads cerrados o perdidos',
    icon: Target,
    color: 'bg-orange-100 text-orange-600'
  }
];

const Reports = () => {
  const { token, isAdmin } = useAuth();
  const [agents, setAgents] = useState([]);
  const [selectedReport, setSelectedReport] = useState('contacts');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [format, setFormat] = useState('xlsx');
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchAgents();
  }, [token]);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAgents(data);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());
      if (selectedAgent !== 'all') params.append('agent_id', selectedAgent);
      params.append('limit', '10');

      const response = await fetch(
        `${API_URL}/api/reports/preview/${selectedReport}?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        toast.error('Error al generar vista previa');
      }
    } catch (error) {
      console.error('Error previewing report:', error);
      toast.error('Error al generar vista previa');
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const body = {
        report_type: selectedReport,
        format: format
      };
      if (startDate) body.start_date = new Date(startDate).toISOString();
      if (endDate) body.end_date = new Date(endDate).toISOString();
      if (selectedAgent !== 'all') body.agent_id = selectedAgent;

      const response = await fetch(`${API_URL}/api/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const blob = await response.blob();
        const filename = response.headers.get('Content-Disposition')?.split('filename=')[1] || 
          `reporte_${selectedReport}.${format}`;
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Reporte descargado exitosamente');
      } else {
        toast.error('Error al generar reporte');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Error al descargar reporte');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Acceso Restringido</h2>
            <p className="text-slate-500">Solo los administradores pueden acceder a los reportes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentReportInfo = REPORT_TYPES.find(r => r.id === selectedReport);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Reportes</h1>
          <p className="text-sm text-slate-500">Genera y descarga reportes en CSV o Excel</p>
        </div>
        <Badge className="bg-[#25D366]/10 text-[#25D366]">
          <FileSpreadsheet className="w-4 h-4 mr-1" />
          Exportación
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Report Selection & Filters */}
        <div className="lg:col-span-1 space-y-4">
          {/* Report Type Selection */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Table className="w-5 h-5 text-[#25D366]" />
                Tipo de Reporte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {REPORT_TYPES.map((report) => {
                const Icon = report.icon;
                return (
                  <button
                    key={report.id}
                    onClick={() => {
                      setSelectedReport(report.id);
                      setPreview(null);
                    }}
                    data-testid={`report-type-${report.id}`}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      selectedReport === report.id
                        ? 'border-[#25D366] bg-[#25D366]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${report.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{report.name}</p>
                        <p className="text-xs text-slate-500">{report.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#25D366]" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Range */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Rango de Fechas</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-400">Desde</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="filter-start-date"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Hasta</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="filter-end-date"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Agent Filter */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Agente</Label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger data-testid="filter-agent">
                    <SelectValue placeholder="Todos los agentes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los agentes</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem key={agent.user_id} value={agent.user_id}>
                        {agent.name} ({agent.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Format Selection */}
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Formato de Descarga</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="filter-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="xlsx">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        Excel (.xlsx)
                      </div>
                    </SelectItem>
                    <SelectItem value="csv">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        CSV (.csv)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSelectedAgent('all');
                }}
                className="w-full"
              >
                Limpiar filtros
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Preview & Download */}
        <div className="lg:col-span-2 space-y-4">
          {/* Action Buttons */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {currentReportInfo && (
                    <>
                      <div className={`p-2 rounded-lg ${currentReportInfo.color}`}>
                        <currentReportInfo.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{currentReportInfo.name}</p>
                        <p className="text-sm text-slate-500">
                          {format === 'xlsx' ? 'Excel' : 'CSV'} 
                          {startDate || endDate ? ` • ${startDate || '...'} - ${endDate || '...'}` : ''}
                          {selectedAgent !== 'all' ? ` • Agente filtrado` : ''}
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewing}
                    data-testid="preview-btn"
                  >
                    {previewing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Vista Previa
                  </Button>
                  <Button
                    onClick={handleDownload}
                    disabled={loading}
                    data-testid="download-btn"
                    className="bg-[#25D366] hover:bg-[#1DA851]"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Descargar {format.toUpperCase()}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Area */}
          <Card className="border-slate-200 min-h-[400px]">
            <CardHeader className="pb-3 border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5 text-slate-400" />
                Vista Previa
                {preview && (
                  <Badge variant="secondary" className="ml-2">
                    {preview.total_records} registros totales
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Muestra los primeros 10 registros del reporte
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              {!preview && !previewing && (
                <div className="flex flex-col items-center justify-center h-[300px] text-slate-400">
                  <Eye className="w-16 h-16 mb-4 text-slate-200" />
                  <p>Haz clic en "Vista Previa" para ver los datos</p>
                </div>
              )}

              {previewing && (
                <div className="flex items-center justify-center h-[300px]">
                  <Loader2 className="w-8 h-8 animate-spin text-[#25D366]" />
                </div>
              )}

              {preview && preview.preview.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        {Object.keys(preview.preview[0]).map((key) => (
                          <th key={key} className="px-3 py-2 text-left font-medium text-slate-600 capitalize">
                            {key.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((row, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          {Object.values(row).map((value, colIdx) => (
                            <td key={colIdx} className="px-3 py-2 text-slate-700">
                              {String(value || '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.total_records > 10 && (
                    <p className="text-center text-sm text-slate-400 mt-4">
                      Mostrando 10 de {preview.total_records} registros. Descarga el reporte completo.
                    </p>
                  )}
                </div>
              )}

              {preview && preview.preview.length === 0 && (
                <div className="flex flex-col items-center justify-center h-[300px] text-slate-400">
                  <FileText className="w-16 h-16 mb-4 text-slate-200" />
                  <p>No hay datos para los filtros seleccionados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Reports;
